import { useState } from 'react';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core';

import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import Button from '~/ui/components/common/Button';
import NoDataFound from '~/ui/components/reusable/NoDataFound';
import MedicationModal from '../popups/MedicationModal';
import Item from './Item';

import { ICarePlanMedication } from '~/services/api/carePlanMedications/types';
import { IPagePermissions } from '../../types';
import styles from './Medications.module.scss';
import getInitialValues from './form/getInitialValues';

interface IProps {
  patientId: number;
  items: ICarePlanMedication[];
  permissions: IPagePermissions;
  onCarePlanChange: () => void;
}

const Medications = ({ patientId, items, permissions, onCarePlanChange }: IProps) => {
  const [showCreate, setShowCreate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const { onCreateCarePlanMedications } = useStoreActions(actions => actions.carePlanMedications);

  const { showError } = useStoreActions(actions => actions.snackbar);

  const handleCreateCarePlanMedications = async (data: ICarePlanMedication[]) => {
    try {
      setIsCreating(true);

      await onCreateCarePlanMedications({ patientId, data });
      onCarePlanChange();
      setShowCreate(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <div className={styles.title}>Medications</div>
        {permissions.canManage && (
          <Button variant="contained" color="primary" onClick={() => setShowCreate(true)}>
            + Add New
          </Button>
        )}
      </div>
      <div className={styles.tableContainer}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="20%">Name</TableCell>
                <TableCell width="10%">Start Date</TableCell>
                <TableCell width="10%">End Date</TableCell>
                <TableCell>Reason</TableCell>
                {permissions.canManage && <TableCell width={120} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length ? (
                items.map(item => (
                  <Item
                    key={item.id}
                    item={item}
                    permissions={permissions}
                    onCarePlanChange={onCarePlanChange}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>
                    <NoDataFound />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      {showCreate && (
        <MedicationModal
          initialValues={getInitialValues({} as ICarePlanMedication)}
          isProcessing={isCreating}
          onSubmit={handleCreateCarePlanMedications}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
};

export default Medications;
